import type { ContentItem } from '@/api/v4/content-items.api';
import { FormFieldError } from '@/components/form/form-field-error';
import { UploadFileInput } from '@/components/upload-file-input/upload-file-input';
import { UploadedFileCard } from '@/components/uploaded-file-card/uploaded-file-card';
import {
  IMAGE_EXTENSIONS,
  WEBP_EXTENSIONS,
} from '@/features/action-panel/files/constants';
import { Stack } from '@/ui/line/line';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  uploadedFile: File | null;
  imageMetadata: ContentItem['image'] | null;
  error?: string;
  onUploadFile: (uploadedFile: File | null) => void;
}

export const ContentItemFormImage = ({
  uploadedFile,
  imageMetadata,
  error,
  onUploadFile,
}: Props) => {
  const { t } = useTranslation('settings', { keyPrefix: 'feedTab' });
  const [imageMetadataDeleted, setImageMetadataDeleted] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const removeFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    onUploadFile(null);
  };

  if (uploadedFile) {
    return (
      <UploadedFileCard
        name={uploadedFile.name}
        size={uploadedFile.size}
        onClose={removeFile}
      />
    );
  }

  if (imageMetadata && !imageMetadataDeleted) {
    return (
      <UploadedFileCard
        name={imageMetadata.name}
        size={imageMetadata.size}
        onClose={() => setImageMetadataDeleted(true)}
      />
    );
  }

  return (
    <Stack gap="8px">
      <UploadFileInput
        acceptedFiles={[...WEBP_EXTENSIONS, ...IMAGE_EXTENSIONS]}
        buttonText={t`articleForm.uploadImage`}
        icon="Plus"
        onClick={() => fileInputRef.current?.click()}
        ref={fileInputRef}
        onUploadFile={e => {
          const file = e.target.files?.[0] ?? null;
          onUploadFile(file);
        }}
      />
      {error && <FormFieldError>{error}</FormFieldError>}
    </Stack>
  );
};
